// 課金商品の台帳。価格はサーバーがここから決める（端末の申告を使わない）。
// 原則：課金で霊玉・兵糧は絶対に付与しない。強さを直接売らない（お守りは遊んでも手に入るものを先に得る、性能は横並び）。
import { CHAR_IDS, type CharId } from '../types';
import { grantOmamori } from './omamori';
import type { OpResult, Profile } from './profile';

export type ProductType = 'permanent' | 'consumable' | 'cosmetic' | 'pass';

export type ProductEffect =
  | { kind: 'entitlement'; key: string } // exp2x / drop2x / recruit3x / auto / speed4 / build_slots / reserve_plus / otasuke / komedoiya
  | { kind: 'ticket'; ticket: 'rename'; count: number }
  | { kind: 'chat'; count: number } // お問い合わせのチャットチケット：購入から SUPPORT.validDays 日の期限。サーバーが grantSupportTickets で付ける（期限に今の時刻が要る）
  | { kind: 'omamori'; defId: string } // 完成品（最大段）で強化不可。販売は終了（RETIRED_PRODUCTS）
  | { kind: 'slots'; packs: number }
  | { kind: 'monthly' } // 月パス：買い切りの30日（自動更新なし）。サーバーが購入ごとに grantMonthly（meta/monthly.ts）で期限を延ばす
  | { kind: 'gacha10' } // おまもりガチャ10連：買ったその場で引く（pullPaidGacha）。券としては残さない
  | { kind: 'kishin' } // 寄進：全員にEXP+20%を30分＋全員に兵糧30（プレゼントボックス。1日 kishin.hyoroPerDay 回まで）。サーバーが全体の効果時刻を延ばす
  | { kind: 'cosmetic'; key: string }
  | { kind: 'gaiden'; charId: CharId; omamori: string };

export interface Product {
  id: string;
  name: string;
  yen: number; // 税込
  type: ProductType;
  effect: ProductEffect;
  /** 1日の販売上限（全体）。寄進のみ */
  dailyGlobalCap?: number;
  desc: string;
}

export const PRODUCTS: readonly Product[] = [
  { id: 'monthly', name: '月パス（30日）', yen: 500, type: 'pass', effect: { kind: 'monthly' }, desc: '30日間、兵糧の回復が3倍速（1日に回復する量は同じ）とチャットのアイコン縁10種。茶屋のたまきと話せるチャットチケット30枚（購入から180日）。あるじの姿＋10種は初回の購入でずっと使えます（買い切り）。チャットの縁は有効期限中のみ適用され、期限が切れると縁なしに戻ります。買うとその場でおまもりガチャ10連。自動では更新しません（続けて買うと期限が30日延びる）' },
  { id: 'exp2x', name: 'EXP2倍（永続）', yen: 980, type: 'permanent', effect: { kind: 'entitlement', key: 'exp2x' }, desc: '戦闘の基本の主EXPが常に2倍。初回クリア・寄進のボーナスは2倍にならず、そのまま足される。上乗せ分は霊玉に換算されない' },
  { id: 'drop2x', name: '装備ドロップ率2倍（永続）', yen: 980, type: 'permanent', effect: { kind: 'entitlement', key: 'drop2x' }, desc: 'すべての戦闘（手動・オート・掃討）で装備のドロップ数が2倍。本日初出撃の2倍と重なる。霊玉は増えない' },
  { id: 'recruit3x', name: '勧誘率3倍（永続）', yen: 980, type: 'permanent', effect: { kind: 'entitlement', key: 'recruit3x' }, desc: 'すべての戦闘（手動・オート・掃討）で式神の勧誘率が3倍。式神の札・式神の社と重なる（掛け算）。霊玉は増えない' },
  // 権利のキーは旧「倍速×4」の speed4 のまま（買った人はそのまま倍速パック）
  { id: 'speed4', name: '倍速パック（永続）', yen: 500, type: 'permanent', effect: { kind: 'entitlement', key: 'speed4' }, desc: '戦闘とオート周回が4倍速に（2倍速はGoogle連携で無料）' },
  { id: 'reserve_plus', name: '兵糧の予備枠+720（永続）', yen: 500, type: 'permanent', effect: { kind: 'entitlement', key: 'reserve_plus' }, desc: '自然回復のあふれを貯める予備の上限が 900 → 1620（2日ぶん多く貯まる）。兵糧そのものは増えない' },
  { id: 'komedoiya', name: '米問屋の通い帳（永続）', yen: 500, type: 'permanent', effect: { kind: 'entitlement', key: 'komedoiya' }, desc: '里の米問屋で、兵糧30を掃討チケット10枚に替えられる' },
  { id: 'otasuke', name: 'おたすけビルド（永続）', yen: 500, type: 'permanent', effect: { kind: 'entitlement', key: 'otasuke' }, desc: '「おすすめビルド」が「おたすけビルド」に。ビルドの方向性（型）を選ぶだけで、スキル・装備・お守り・道具・式神をその方向に合わせて自動でセット。主Lvが上がるたびにスキルも組み直す。強さは自分で組んだときと同じ' },
  { id: 'build_slots', name: 'スキル構成の保存枠', yen: 200, type: 'permanent', effect: { kind: 'entitlement', key: 'build_slots' }, desc: 'スキル構成の保存枠がキャラごとに10枠になる（無料は1枠）。スキルの組み替え自体はいつでも無料' },
  { id: 'slots', name: '所持枠+50', yen: 200, type: 'permanent', effect: { kind: 'slots', packs: 1 }, desc: '装備の所持枠を50増やす' },
  { id: 'gacha10', name: 'おまもりガチャ10連', yen: 1000, type: 'consumable', effect: { kind: 'gacha10' }, desc: '買うとその場で10連を引く（ランクA以上1回確定）。ガチャ券としては残りません' },
  { id: 'chat10', name: 'チャットチケット10枚', yen: 100, type: 'consumable', effect: { kind: 'chat', count: 10 }, desc: '茶屋のたまき（AI）と話すときに、1回の返事で1枚使う（Google連携したアカウントは最初の10回まで無料）。有効期限は購入から180日（過ぎた分は消えます）' },
  { id: 'kishin', name: '寄進', yen: 300, type: 'consumable', effect: { kind: 'kishin' }, dailyGlobalCap: 20, desc: '名前がチャットに出て、全員の戦闘の主EXP+20%（基本の主EXPの20%）を30分・全員に兵糧30（兵糧はその日の最初の3回の寄進まで）' },
];

/**
 * 販売をやめた商品（お守りの個別販売、2026-10-14）。新しくは買えない（PRODUCTS・PRODUCT_BY_ID に無い）が、
 * 買った人の注文履歴の表示・返金（revokeProduct）・決済途中の Webhook のために定義は残す。お守りはおまもりガチャ・章ボス・星報酬・図鑑・外伝で手に入る
 */
export const RETIRED_PRODUCTS: readonly Product[] = [
  // 名前変更はいつでも無料に（2026-10-30）
  { id: 'rename', name: '名前変更券', yen: 100, type: 'consumable', effect: { kind: 'ticket', ticket: 'rename', count: 1 }, desc: '' },
  // オートは Google 連携していれば無料に（2026-10-21）
  { id: 'auto', name: 'オート周回（永続）', yen: 980, type: 'permanent', effect: { kind: 'entitlement', key: 'auto' }, desc: '新しいステージも含めてAIに任せて進める' },
  { id: 'om_sai2', name: 'お守り：賽の目守', yen: 500, type: 'permanent', effect: { kind: 'omamori', defId: 'sai2' }, desc: '完成品（最大段）。遊んでも手に入る' },
  { id: 'om_nidobushin', name: 'お守り：二度建て守', yen: 500, type: 'permanent', effect: { kind: 'omamori', defId: 'nidobushin' }, desc: '完成品（最大段）。遊んでも手に入る' },
  { id: 'om_hayagaeri', name: 'お守り：早帰り守', yen: 300, type: 'permanent', effect: { kind: 'omamori', defId: 'hayagaeri' }, desc: '完成品（最大段）。遊んでも手に入る' },
  { id: 'om_jintori', name: 'お守り：陣取り守', yen: 300, type: 'permanent', effect: { kind: 'omamori', defId: 'jintori' }, desc: '完成品（最大段）。遊んでも手に入る' },
];

/**
 * まだ作っていない商品（外伝・見た目）。中身ができるまでショップに出さず、買えない（PRODUCTS・PRODUCT_BY_ID に無い）。
 * できたら PRODUCTS へ戻す
 */
export const UNRELEASED_PRODUCTS: readonly Product[] = [
  { id: 'gaiden_akari', name: '外伝：アカリ', yen: 500, type: 'permanent', effect: { kind: 'gaiden', charId: 'akari', omamori: 'g_akari' }, desc: '5話＋専用戦闘。報酬は見た目違いのお守り' },
  { id: 'gaiden_kazamaru', name: '外伝：カザマル', yen: 500, type: 'permanent', effect: { kind: 'gaiden', charId: 'kazamaru', omamori: 'g_kazamaru' }, desc: '' },
  { id: 'gaiden_suzune', name: '外伝：スズネ', yen: 500, type: 'permanent', effect: { kind: 'gaiden', charId: 'suzune', omamori: 'g_suzune' }, desc: '' },
  { id: 'gaiden_yuzuha', name: '外伝：ユズハ', yen: 500, type: 'permanent', effect: { kind: 'gaiden', charId: 'yuzuha', omamori: 'g_yuzuha' }, desc: '' },
  { id: 'skin_akari', name: '着せ替え：アカリ', yen: 500, type: 'cosmetic', effect: { kind: 'cosmetic', key: 'skin:akari_1' }, desc: '立ち絵＋盤面の駒' },
  { id: 'skin_kazamaru', name: '着せ替え：カザマル', yen: 500, type: 'cosmetic', effect: { kind: 'cosmetic', key: 'skin:kazamaru_1' }, desc: '' },
  { id: 'skin_suzune', name: '着せ替え：スズネ', yen: 500, type: 'cosmetic', effect: { kind: 'cosmetic', key: 'skin:suzune_1' }, desc: '' },
  { id: 'skin_yuzuha', name: '着せ替え：ユズハ', yen: 500, type: 'cosmetic', effect: { kind: 'cosmetic', key: 'skin:yuzuha_1' }, desc: '' },
  { id: 'voice', name: 'ボイスパック', yen: 300, type: 'cosmetic', effect: { kind: 'cosmetic', key: 'voice:1' }, desc: '' },
  { id: 'design_yagura', name: '建物の意匠：櫓', yen: 300, type: 'cosmetic', effect: { kind: 'cosmetic', key: 'design:yagura_1' }, desc: '' },
];

/** 販売中の商品（ショップ・Checkout が使う） */
export const PRODUCT_BY_ID: Record<string, Product> = Object.fromEntries(PRODUCTS.map((p) => [p.id, p]));
/** 販売をやめた商品も含めた台帳（注文の表示・Webhook・返金が使う） */
export const PRODUCT_RECORD_BY_ID: Record<string, Product> = Object.fromEntries([...PRODUCTS, ...RETIRED_PRODUCTS, ...UNRELEASED_PRODUCTS].map((p) => [p.id, p]));

/**
 * 購入の反映（profile のみ。資源には一切触れない＝霊玉・兵糧を付与しない）。
 * kishin は全体の効果なので profile は変えず、サーバーが全体の効果時刻を延ばす（kishinUntil）。
 */
export function grantProduct(p: Profile, productId: string): OpResult<{ product: Product; changed: boolean }> {
  const product = PRODUCT_RECORD_BY_ID[productId];
  if (!product) return { ok: false, error: 'unknown_product' };
  const e = product.effect;
  const ent = (key: string) => {
    if (p.entitlements.includes(key)) return false;
    p.entitlements.push(key);
    return true;
  };
  let changed = true;
  switch (e.kind) {
    case 'entitlement':
    case 'cosmetic':
      changed = ent(e.key);
      break;
    case 'ticket':
      p.tickets[e.ticket] += e.count;
      break;
    case 'omamori':
      changed = ent(`omamori:${e.defId}`);
      if (changed) grantOmamori(p, e.defId, true);
      break;
    case 'slots':
      p.slotPacks += e.packs;
      break;
    case 'gaiden':
      changed = ent(`gaiden:${e.charId}`);
      // 外伝のお守りは外伝をクリアしたときに grantOnce('gaiden:<char>') で付与する
      break;
    case 'kishin':
    case 'chat':
    case 'monthly':
    case 'gacha10':
      changed = false;
      break;
  }
  return { ok: true, product, changed };
}

/** 返金・チャージバック時の剥奪（永続・見た目のみ。消耗品は使用済みの分は戻さない） */
export function revokeProduct(p: Profile, productId: string): OpResult {
  const product = PRODUCT_RECORD_BY_ID[productId];
  if (!product) return { ok: false, error: 'unknown_product' };
  const e = product.effect;
  const drop = (key: string) => (p.entitlements = p.entitlements.filter((x) => x !== key));
  if (e.kind === 'entitlement' || e.kind === 'cosmetic') drop(e.key);
  if (e.kind === 'gaiden') drop(`gaiden:${e.charId}`);
  if (e.kind === 'omamori') {
    drop(`omamori:${e.defId}`);
    const item = p.omamori.find((x) => x.defId === e.defId && x.paid);
    if (item) {
      p.omamori = p.omamori.filter((x) => x !== item);
      for (const c of CHAR_IDS) p.omamoriSets[c] = p.omamoriSets[c].map((id) => (id === item.id ? null : id));
    }
  }
  if (e.kind === 'ticket') p.tickets[e.ticket] = Math.max(0, p.tickets[e.ticket] - e.count);
  if (e.kind === 'slots') p.slotPacks = Math.max(0, p.slotPacks - e.packs);
  return { ok: true };
}
